import { useQuery } from '@apollo/client';
import { plural, Trans } from '@lingui/macro';
import ReactHtmlParser from 'html-react-parser';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import AdminMessageController from 'Components/AdminMessage/AdminMessageController';
import BigCities from 'Components/BigCities/BigCities';
import CallToActionSection from 'Components/CallToActionSection/CallToActionSection';
import CategoriesSlider from 'Components/CategoriesSlider/CategoriesSlider';
import { CurrentUserConsumer, TCurrentUserState } from 'Components/CurrentUser/CurrentUser';
import SeoLinking from 'Components/SeoLinking/SeoLinking';
import PageView from 'common/PageView/PageView';
import 'common/styles/SlickSlider.scss';
import config from 'config/config';
import seoLinking from 'config/seoLinking';
import { Heading3, Heading4 } from 'uikit/Heading';
import { TextP1 } from 'uikit/Text';
import analytics from 'utils/analytics/analytics';
import { useL4BySlugQuery } from 'utils/graphql/categories';
import routes from 'utils/routeTranslator';

import BreadcrumbSchema from './BreadcrumbSchema';
import {
  l4Redirects,
  l4Redirect,
  l2Query,
  getInterlinkingRelatedServices,
  useRelatedCompanies,
  RelatedByL2Vars,
  RelatedByL2Response,
  buildRegisterPageLinking,
} from './L4Category.helpers';
import './L4Category.scss';
import L4CategoryTopSection from './L4CategoryTopSection';

type Replacement = string | number | null;

type Replacements = {
  [key: string]: Replacement | null | undefined;
};

const applyReplacements = (message: string, replacements: Replacements): string => {
  for (const replace in replacements) {
    message = message.split(`:${replace}`).join(String(replacements[replace]));
  }
  return message;
};

const L4Category: FC = () => {
  const params = useParams<{ slug: string }>();
  const location = useLocation();
  const l4RedirectSlug = l4Redirects[params.slug as keyof typeof l4Redirects];

  const { data } = useL4BySlugQuery({
    variables: {
      slug: params.slug,
    },
    skip: !!l4RedirectSlug,
  });

  /**
   * SEO:
   * GS-8269 Change interlinking in the "Powiązane usługi" block
   * see https://naspersclassifieds.atlassian.net/browse/GS-8269
   * Extend the interlinking of "zlecenia pages" on L4
   * see https://naspersclassifieds.atlassian.net/browse/GS-8621
   */
  const l2Slug = data?.l4BySlug.l2.slug;
  const { data: l2Data } = useQuery<RelatedByL2Response, RelatedByL2Vars>(l2Query, {
    skip: !l2Slug,
    variables: {
      slug: l2Slug,
    },
  });

  const relatedCompanies = useRelatedCompanies(data?.l4BySlug.companyListingSlug);

  if (l4RedirectSlug) {
    return l4Redirect(l4RedirectSlug);
  }

  if (!data || !l2Data) return null;

  const { l4BySlug } = data;

  const relatedServices = getInterlinkingRelatedServices(l2Data.l2BySlug?.l4Groups);
  const registrationPages = buildRegisterPageLinking(l2Data.l2BySlug.categories);

  const { id, name, slug, siteTitleTemplate, l2, l3, providerCount, description, seoDescription, companyListingSlug } =
    l4BySlug;

  const seoText: string = description;
  const providersCount: number = providerCount < 10 ? 10 : providerCount;
  const now = new Date();
  const monthNumber = now.getMonth() + 1;
  const monthLocativeCase: string = plural(monthNumber, {
    1: 'w styczniu',
    2: 'w lutym',
    3: 'w marcu',
    4: 'w kwietniu',
    5: 'w maju',
    6: 'w czerwcu',
    7: 'w lipcu',
    8: 'w sierpniu',
    9: 'we wrześniu',
    10: 'w październiku',
    11: 'w listopadzie',
    12: 'w grudniu',
  });
  const year = now.getFullYear();
  const l3Slug = l3.slug;

  const siteSeoTitle: string = applyReplacements(siteTitleTemplate, {
    providersCount,
    month: monthLocativeCase,
    year,
  });

  return (
    <CurrentUserConsumer>
      {(context: TCurrentUserState | undefined) => {
        if (context?.currentUser?.isProvider) {
          // Redirect SP
          return <Redirect to={routes.get('front::dashboard')} />;
        }

        return (
          <div className="l4Category">
            <Helmet>
              <title>{siteSeoTitle}</title>
              <meta property="og:title" content={siteSeoTitle} />
              <meta property="og:description" content={seoDescription} />
              <meta property="og:image" content={l4BySlug.mobileCoverUrl} />
              <meta name="description" content={seoDescription} />
              <meta name="thumbnail" content={l4BySlug.mobileCoverUrl} />
              <link rel="canonical" href={config.SITE_URL + routes.get('front::category', { slug })} />
              <body className="mobileNotFixedHeader" />
            </Helmet>
            {/* TODO: Implement */}
            {/* <L4Critical /> */}
            <PageView
              key={slug}
              pageLabel="view_list_professional_profile"
              l2_id={parseInt(l2.id, 10)}
              l3_id={parseInt(l3.id, 10)}
              l4_id={parseInt(id, 10)}
              l2_slug={l2.slug}
              l3_slug={l3.slug}
              l4_slug={slug}
            />

            <L4CategoryTopSection l4BySlug={l4BySlug} hash={location.hash} />

            {companyListingSlug && (
              <CallToActionSection
                tagline={name}
                title={<Trans>Wybierz jednego z najlepiej ocenianych Wykonawców</Trans>}
                action={<Trans>Przejdź do listy</Trans>}
                href={routes.get('front::companies.l4', {
                  slug: companyListingSlug,
                })}
                onClick={() => analytics.trackEvent('firma_l4_browse', { l4_slug: slug })}
              />
            )}

            <div className="l4Category__sectionBottom">
              <div className="l4Category__relatedCategories">
                <div className="l4Category__subtitle l4Category__subtitle_related">
                  <Heading3 as="h2">
                    {name}: <Trans>Powiązane usługi</Trans>
                  </Heading3>
                </div>
                <CategoriesSlider categories={relatedServices} />
              </div>

              <div className="l4Category__seoBlock">
                {seoText && (
                  <div className="l4Category__seo">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">{name}</Heading3>
                    </div>

                    <div className="l4Category__seoText">{ReactHtmlParser(seoText)}</div>
                    {l3.hasSpRegistration && (
                      <div className="l4Category__l3Register">
                        <div className="l4Category__l3RegisterTitle">
                          <Heading4 as="h3">
                            <span className="l4Category__l3RegisterTitleIcon"></span>
                            <Trans>Wykonujesz tę usługę?</Trans>
                          </Heading4>
                        </div>
                        <TextP1>
                          <Trans id="l4category.l3_register_text">
                            Dzięki nam możesz nie tylko otrzymywać wiele ciekawych zleceń, ale także podejmować się ich
                            {/* eslint-disable-next-line max-len */}
                            realizacji. Zarejestruj się jako wykonawca na Fixly i zacznij zarabiać, świadcząc usługę{' '}
                            <a href={routes.get('front::provider.register.l3', { slug: l3Slug })}>{name}</a>! Codziennie
                            na naszej platformie publikowane są setki zleceń w różnych kategoriach na terenie całej
                            Polski. Zdobywanie nowych klientów jeszcze nigdy nie było tak proste. Dołącz do grona
                            profesjonalistów na Fixly i już teraz zwiększ swoje dochody!
                          </Trans>
                        </TextP1>
                      </div>
                    )}
                  </div>
                )}

                <div className="l4Category__seo">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>Wykonawcy w różnych miastach</Trans>
                    </Heading3>
                  </div>
                  <BigCities categorySlug={slug} categoryName={name} multiColumn />
                </div>

                {relatedCompanies && (
                  <div className="l4Category__seo">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">
                        <Trans>Firmy w powiązanych usługach</Trans>
                      </Heading3>
                    </div>
                    <SeoLinking links={relatedCompanies} />
                  </div>
                )}
                {registrationPages && (
                  <div className="l4Category__seo">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">
                        <Trans>Powiązane zlecenia</Trans>
                      </Heading3>
                    </div>
                    <SeoLinking links={registrationPages} />
                  </div>
                )}
              </div>

              {seoLinking && seoLinking[location.pathname] && (
                <div className="l4Category__seoLinking">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>Najpopularniejsze wyszukiwania</Trans>
                    </Heading3>
                  </div>
                  <SeoLinking links={seoLinking[location.pathname]} />
                </div>
              )}
            </div>
            {context?.currentUser && <AdminMessageController />}
            <BreadcrumbSchema l4Category={l4BySlug} />
          </div>
        );
      }}
    </CurrentUserConsumer>
  );
};

export default L4Category;
